<template>
  <div v-if="account">
    <AccountListItem :detail="true" :account="account" />

    <!-- Mobile View Tabs -->
    <v-tabs
      v-if="isMobile"
      v-model="tabs"
      grow
      bg-color="primary"
      slider-color="secondary"
    >
      <v-tab value="tab-1">
        <v-icon start>mdi-clipboard-text</v-icon>
        Verslagen
      </v-tab>
      <v-tab value="tab-2">
        <v-icon start>mdi-account-details</v-icon>
        Info
      </v-tab>
      <v-tab value="tab-3">
        <v-icon v-if="pastDeadlinesCount > 0" start>mdi-alert</v-icon>
        <v-icon v-else start>mdi-calendar-check</v-icon>
        Taken
        <v-badge
          v-if="deadlines.length > 0"
          :content="deadlines.length"
          color="info"
          inline
        ></v-badge>
      </v-tab>
    </v-tabs>

    <!-- Mobile Tab Content -->
    <v-window v-if="isMobile" v-model="tabs">
      <v-window-item value="tab-1">
        <!-- Account reports with mobile-optimized container -->
        <div class="mobile-reports-container">
          <AccountReports ref="accountReportsMobile" :account_id="account.id" />
        </div>
      </v-window-item>

      <v-window-item value="tab-2">
        <!-- Stored AI briefing (beta testers only) -->
        <div v-if="canUseAi" class="pa-2 pb-0">
          <AiAccountBriefing :account-id="account.id" />
        </div>
        <!-- Account details - using new sidebar component -->
        <AccountSidebar
          :account="account"
          :deadlines="deadlines"
          :past-deadlines-count="pastDeadlinesCount"
          :has-enriched-location="hasEnrichedLocation"
          :hide-deadlines="true"
          @edit="editAccount"
          @add-deadline="addDeadline"
          @open-nearby-contacts="openNearbyContacts"
        />
      </v-window-item>

      <v-window-item value="tab-3">
        <!-- Tasks tab - using AccountSidebar with only deadlines visible -->
        <AccountSidebar
          :account="account"
          :deadlines="deadlines"
          :past-deadlines-count="pastDeadlinesCount"
          :has-enriched-location="hasEnrichedLocation"
          :show-only-deadlines="true"
          @edit="editAccount"
          @add-deadline="addDeadline"
          @open-nearby-contacts="openNearbyContacts"
        />
      </v-window-item>
    </v-window>

    <!-- Desktop View -->
    <v-container v-else fluid>
      <v-row>
        <v-col lg="6">
          <v-card class="mb-4">
            <v-card-title class="bg-primary-lighten-5 d-flex align-center">
              <v-icon start color="primary" class="mr-2"
                >mdi-clipboard-text</v-icon
              >
              <span>Verslagen</span>
            </v-card-title>
            <v-card-text class="pa-0">
              <AccountReports
                ref="accountReportsDesktop"
                :account_id="account.id"
              />
            </v-card-text>
          </v-card>
        </v-col>

        <v-col lg="6">
          <!-- Stored AI briefing (beta testers only) -->
          <AiAccountBriefing v-if="canUseAi" :account-id="account.id" />
          <!-- New Redesigned Sidebar -->
          <AccountSidebar
            :account="account"
            :deadlines="deadlines"
            :past-deadlines-count="pastDeadlinesCount"
            :has-enriched-location="hasEnrichedLocation"
            @edit="editAccount"
            @add-deadline="addDeadline"
            @open-nearby-contacts="openNearbyContacts"
          />
        </v-col>
      </v-row>
    </v-container>

    <!-- Nearby Contacts Dialog -->
    <NearbyContactsDialog
      v-model="showNearbyContactsDialog"
      :contacts="nearbyContactsList"
      :loading="nearbyContactsLoading"
      :error="nearbyContactsError"
    />
  </div>
</template>

<script>
import AccountListItem from "./AccountListItem.vue";
import AccountSidebar from "./AccountSidebar.vue";
import AccountReportsRefactor from "./AccountReportsRefactor.vue";
import NearbyContactsDialog from "../contacts/NearbyContactsDialog.vue";
import AiAccountBriefing from "../ai/AiAccountBriefing.vue";
import { useAiAccess } from "@/composables/useAiAccess";

import { useAccountsStoreRefactor } from "@/stores/accountsStoreRefactor";
import { useDeadlinesStoreRefactor } from "@/stores/deadlinesStoreRefactor";
import { useContextMenuStore } from "@/stores/contextMenuStore";
import { useMenuStore } from "@/stores/menuStore";
import { computed, ref, watch, onMounted, onUnmounted } from "vue";
import { useFormsStore } from "@/stores/formsStore";
import { useRoute } from "vue-router";
import { useAutoFetchById } from "@/composables/useAutoFetch";
import { useNearbyContacts } from "@/composables/useNearbyContacts";

export default {
  components: {
    AccountListItem,
    AccountReports: AccountReportsRefactor,
    AccountSidebar,
    NearbyContactsDialog,
    AiAccountBriefing,
  },
  setup() {
    const { canUseAi } = useAiAccess();
    const accountsStore = useAccountsStoreRefactor();
    const deadlinesStore = useDeadlinesStoreRefactor();
    const contextMenuStore = useContextMenuStore();
    const menuStore = useMenuStore();
    const formsStore = useFormsStore();
    const route = useRoute();
    const nearbyContacts = useNearbyContacts();
    const showNearbyContactsDialog = ref(false);

    const tabs = ref("tab-1");
    const infiniteId = ref(1);
    const new_report = ref({
      report_category: "Locatie",
    });
    const isMobile = ref(false);
    const accountReportsMobile = ref(null);
    const accountReportsDesktop = ref(null);

    // Account ID from route
    const accountId = computed(() => Number(route.params.accountId));

    // Auto-fetch account data reactively
    const {
      data: account,
      loading: accountLoading,
      error: accountError,
    } = useAutoFetchById(accountsStore, accountId);

    // Use store getters for deadline data (assumes app.vue already loads all deadlines)
    const deadlines = computed(() =>
      deadlinesStore.getDeadlinesByAccount(accountId.value),
    );

    const pastDeadlinesCount = computed(() =>
      deadlinesStore.getPastDeadlinesCountByAccount(accountId.value),
    );

    // Methods
    const onResize = () => {
      // Check against big screen sizes
      isMobile.value = window.innerWidth < 1264; // lg breakpoint in Vuetify
    };

    const refreshReports = () => {
      console.log("AccountDetail: Refreshing reports");
      console.log("AccountDetail: Mobile ref:", accountReportsMobile.value);
      console.log("AccountDetail: Desktop ref:", accountReportsDesktop.value);
      console.log("AccountDetail: isMobile:", isMobile.value);

      if (accountReportsMobile.value) {
        console.log("AccountDetail: Calling mobile refreshReports");
        accountReportsMobile.value.refreshReports();
      }
      if (accountReportsDesktop.value) {
        console.log("AccountDetail: Calling desktop refreshReports");
        accountReportsDesktop.value.refreshReports();
      }

      if (!accountReportsMobile.value && !accountReportsDesktop.value) {
        console.warn("AccountDetail: No AccountReports refs available!");
      }
    };

    const addDeadline = () => {
      formsStore.edit({
        type: "deadline",
        item: { account_id: accountId.value },
      });
    };

    const editAccount = () => {
      formsStore.edit({
        type: "account",
        item: JSON.parse(JSON.stringify(account.value)),
      });
    };

    // No longer needed - filter store handles trait filtering directly

    // Context menu definition
    const context_menu = [
      {
        name: "Rapporten Verversen",
        action: () => {
          console.log("AccountDetail: Context menu refresh action triggered");
          refreshReports();
        },
        icon: "mdi-refresh",
      },
      {
        name: "Gebruiker wijzigen",
        action: editAccount,
        icon: "mdi-account-edit",
      },
      {
        name: "Deadline toevoegen",
        action: addDeadline,
        icon: "mdi-calendar-check",
      },
    ];

    // Lifecycle hooks
    onMounted(() => {
      console.log("AccountDetail: Component mounted");
      console.log("AccountDetail: Initial refs state:", {
        mobile: accountReportsMobile.value,
        desktop: accountReportsDesktop.value,
        isMobile: isMobile.value,
      });

      // Data loading is handled automatically by useAutoFetchById
      contextMenuStore.addOption(context_menu);
      menuStore.setMenuVisible(true);
      menuStore.setBackActive(true);
      onResize();
      window.addEventListener("resize", onResize, { passive: true });

      // Check refs after a short delay to see if they get set
      setTimeout(() => {
        console.log("AccountDetail: Refs after delay:", {
          mobile: accountReportsMobile.value,
          desktop: accountReportsDesktop.value,
          isMobile: isMobile.value,
        });
      }, 1000);
    });

    // Computed property to check if account has enriched location
    const hasEnrichedLocation = computed(() => {
      if (!account.value) return false;

      // Check if account has a primary location with lat/lng
      const linkedLocations = account.value.linked_locations || [];
      const primaryLocation = linkedLocations.find((ll) => ll.primary);

      if (!primaryLocation || !primaryLocation.location) return false;

      const location = primaryLocation.location;
      return !!(location.latitude && location.longitude);
    });

    // Computed properties for nearby contacts (reactive unwrapping)
    const nearbyContactsList = computed(() => nearbyContacts.contacts.value);
    const nearbyContactsLoading = computed(() => nearbyContacts.loading.value);
    const nearbyContactsError = computed(() => nearbyContacts.error.value);

    // Method to open nearby contacts dialog
    const openNearbyContacts = () => {
      if (!hasEnrichedLocation.value) return;
      showNearbyContactsDialog.value = true;
      nearbyContacts.openDialog(accountId.value);
    };

    watch(showNearbyContactsDialog, (value) => {
      if (!value) {
        nearbyContacts.closeDialog();
      }
    });

    onUnmounted(() => {
      window.removeEventListener("resize", onResize);
      menuStore.setBackActive(false);
      contextMenuStore.reset(); // Clear context menu when component unmounts
    });

    return {
      canUseAi,
      tabs,
      infiniteId,
      new_report,
      isMobile,
      accountReportsMobile,
      accountReportsDesktop,
      account,
      deadlines,
      pastDeadlinesCount,
      onResize,
      refreshReports,
      addDeadline,
      editAccount,
      accountId,
      // Expose loading and error from auto-fetch composable
      loading: accountLoading,
      error: accountError,
      // Nearby contacts functionality
      nearbyContacts,
      hasEnrichedLocation,
      openNearbyContacts,
      nearbyContactsList,
      nearbyContactsLoading,
      nearbyContactsError,
      showNearbyContactsDialog,
      // Handle report updates from App.vue
      handleReportUpdated: (report) => {
        console.log(
          "AccountDetail: Report updated, refreshing reports:",
          report,
        );
        refreshReports();
      },
    };
  },
};
</script>

<style>
/* 📱 MOBILE-ONLY: Remove extra padding from reports container */
@media (max-width: 599px) {
  .mobile-reports-container {
    /* Negative margins to counteract v-card-text padding (16px default) */
    margin: 0;
  }
}
</style>
